import { getLogger } from '@trimble/logging';

interface TaskDef<T> {
  key: string;
  task(): Promise<T>;
  cancelable: boolean;
  abortController: AbortController;
  shouldRun?(): boolean;
  /*
   * A simple priority system where lower numbers indicate higher priority
   * */
  priority(): number;
}

interface TaskDefWrapper<T> extends TaskDef<T> {
  taskWrapper(): Promise<void>;
  rejectTask(error: Error): void;
  enqueuedAt: number;
  startedAt: number | undefined;
  aborted: boolean;
  abortReason: AbortReason | undefined;
}

const DEFAULT_CONCURRENCY = 8;
const TASK_TIMEOUT_MS = 10_000;
const DEBUG_TASK_LIMIT = 100;
const THROUGHPUT_HISTORY_RETENTION_MS = 10_000;
const MS_PER_SECOND = 1000;
const ACTIVE_PREEMPTION_MIN_PRIORITY_IMPROVEMENT = 10;

export class QueueCapacityError extends Error {
  constructor(key: string) {
    super(`Queue capacity reached for task: ${key}`);
    this.name = 'QueueCapacityError';
  }
}

type QueueTaskDebugInfo = {
  key: string;
  position: number | null;
  ageMs: number;
  priority: number;
  aborted: boolean;
  abortReason: AbortReason | undefined;
};

export type AbortReason =
  | 'visibility'
  | 'preemption'
  | 'timeout'
  | 'clear'
  | 'staleBeforeStart'
  | 'remove'
  | 'abortController'
  | 'capacityEviction';

type AbortCounts = Record<AbortReason, number>;

export type PromiseQueueDebugSnapshot = {
  type: string;
  queued: number;
  active: number;
  capacity: number | undefined;
  completed: number;
  completedInWindow: number;
  averageCompletedRequestsPerSecond: number;
  abortCounts: AbortCounts;
  abortCountsInWindow: AbortCounts;
  queuedAbortCounts: AbortCounts;
  queuedAbortCountsInWindow: AbortCounts;
  activeAbortCounts: AbortCounts;
  activeAbortCountsInWindow: AbortCounts;
  queuedTasks: QueueTaskDebugInfo[];
  activeTasks: QueueTaskDebugInfo[];
};

type AbortEvent = {
  timestamp: number;
  reason: AbortReason;
  lifecycle: 'queued' | 'active';
};

export class PromiseQueue<T> {
  private queue: TaskDefWrapper<T>[] = [];
  private activeCount = 0;
  private completedCount = 0;
  private readonly completedTaskTimestamps: number[] = [];
  private readonly abortEvents: AbortEvent[] = [];
  private readonly activeTasks: Map<string, TaskDefWrapper<T>> = new Map();
  private readonly taskPromises: Map<string, Promise<T>> = new Map();
  private readonly abortCounts: AbortCounts = this.createEmptyAbortCounts();
  private readonly queuedAbortCounts: AbortCounts =
    this.createEmptyAbortCounts();
  private readonly activeAbortCounts: AbortCounts =
    this.createEmptyAbortCounts();
  private readonly concurrency: number;

  /*
   * @param type - Type of items processed by the queue
   * @param concurrency - Maximum number of concurrent tasks (default: 8)
   * @param maxQueuedLength - Maximum number of queued tasks. Active tasks are not counted.
   * */
  constructor(
    concurrency = DEFAULT_CONCURRENCY,
    public type: string = 'default',
    private readonly maxQueuedLength: number | undefined = undefined
  ) {
    this.concurrency = concurrency;
  }

  public static genKey(type: string, x: number, y: number, z: number): string {
    return `${type}:x${x}-y${y}-z${z}`;
  }

  public get length(): number {
    return this.queue.length;
  }

  public get activeLength(): number {
    return this.activeCount;
  }

  public removeWhere(
    predicate: (task: TaskDefWrapper<T>) => boolean,
    abortReason: AbortReason = 'remove'
  ): void {
    this.removeQueuedWhere(
      (task) => predicate(task) && task.cancelable,
      abortReason
    );

    for (const task of this.activeTasks.values()) {
      if (predicate(task) && task.cancelable) {
        this.abortTask(task, abortReason);
      }
    }
  }

  public remove(key: string, abortReason: AbortReason = 'remove'): void {
    this.removeQueuedWhere(
      (task) => task.key === key && task.cancelable,
      abortReason
    );

    const activeTask = this.activeTasks.get(key);
    if (activeTask && activeTask.cancelable) {
      this.abortTask(activeTask, abortReason);
    }
  }

  /** Only reallocates the queue when a task actually matches. */
  private removeQueuedWhere(
    shouldRemove: (task: TaskDefWrapper<T>) => boolean,
    abortReason: AbortReason
  ): void {
    if (!this.queue.some(shouldRemove)) {
      return;
    }

    const tasksToKeep: TaskDefWrapper<T>[] = [];
    for (const task of this.queue) {
      if (shouldRemove(task)) {
        this.abortTask(task, abortReason);
      } else {
        tasksToKeep.push(task);
      }
    }
    this.queue = tasksToKeep;
  }

  public removeByAbortController(
    abortController: AbortController,
    abortReason: AbortReason = 'abortController'
  ): void {
    this.removeWhere(
      (task) => task.abortController === abortController,
      abortReason
    );
  }

  public clear(): void {
    const tasks = [...this.queue];
    this.queue = [];
    const activeTasks = this.activeTasks.values();
    [...tasks, ...activeTasks].forEach((task) => {
      this.abortTask(task, 'clear');
    });
  }

  public reprioritize(): void {
    if (this.queue.length === 0) {
      return;
    }

    const highestPriority = this.sortQueueByPriority();
    this.preemptActiveTaskIfUseful(highestPriority);
    this.startQueuedTasks();
  }

  public add(taskDef: TaskDef<T>): Promise<T> {
    const existingTaskPromise = this.taskPromises.get(taskDef.key);
    if (existingTaskPromise) {
      return existingTaskPromise;
    }

    let admitted = false;
    const addPromise = new Promise<T>((resolve, reject) => {
      const taskDefWrapper: TaskDefWrapper<T> = {
        ...taskDef,
        enqueuedAt: performance.now(),
        startedAt: undefined,
        aborted: false,
        abortReason: undefined,
        rejectTask: reject,
        taskWrapper: (): Promise<void> => {
          this.activeCount++;
          this.activeTasks.set(taskDef.key, taskDefWrapper);
          taskDefWrapper.startedAt = performance.now();

          const timeoutSymbol = Symbol('timeout');
          let timeoutId: ReturnType<typeof setTimeout>;
          const timeoutPromise = new Promise<symbol>((res) => {
            timeoutId = setTimeout(() => {
              res(timeoutSymbol);
            }, TASK_TIMEOUT_MS);
          });

          const racePromise = Promise.race([taskDef.task(), timeoutPromise])
            .then((winner) => {
              if (winner === timeoutSymbol) {
                this.abortCounts.timeout++;
                taskDefWrapper.abortReason = 'timeout';
                taskDef.abortController.abort();
                getLogger().info(
                  `Timeout - Queue length: ${this.queue.length}, Active count: ${this.activeCount}`
                );
                reject(
                  new Error(
                    `Timeout after ${TASK_TIMEOUT_MS}ms for task: ${taskDef.key}`
                  )
                );
              } else if (
                taskDefWrapper.aborted ||
                taskDef.abortController.signal.aborted
              ) {
                reject(this.createAbortError(taskDef.key));
              } else {
                this.recordTaskCompleted();
                resolve(winner as T);
              }
            })
            .catch((error: unknown) => {
              // Handle task failure (rejection from taskDef.task())
              reject(error as Error);
            })
            .finally(() => {
              clearTimeout(timeoutId);
              this.activeCount--;
              this.activeTasks.delete(taskDef.key);
              this.taskPromises.delete(taskDef.key);
              this.run();
            });
          return racePromise;
        },
      };
      if (!this.admit(taskDefWrapper)) {
        reject(this.createCapacityError(taskDef.key));
        return;
      }
      this.preemptActiveTaskIfUseful(taskDefWrapper.priority());
      this.queue.push(taskDefWrapper);
      admitted = true;
      this.run();
    });
    if (admitted) {
      this.taskPromises.set(taskDef.key, addPromise);
    }
    addPromise.catch(() => {
      // The rejection is already passed to the user-facing promise's `reject` function.
      // This just prevents a "fire-and-forget" promise from crashing the process.
    });
    return addPromise;
  }

  public getDebugSnapshot(
    throughputWindowMs: number = MS_PER_SECOND
  ): PromiseQueueDebugSnapshot {
    const now = performance.now();
    const completedInWindow = this.getCompletedTaskCountInWindow(
      now,
      throughputWindowMs
    );
    const abortCountsInWindow = this.getAbortCountsInWindow(
      now,
      throughputWindowMs
    );
    const queuedAbortCountsInWindow = this.getAbortCountsInWindow(
      now,
      throughputWindowMs,
      'queued'
    );
    const activeAbortCountsInWindow = this.getAbortCountsInWindow(
      now,
      throughputWindowMs,
      'active'
    );

    return {
      type: this.type,
      queued: this.queue.length,
      active: this.activeCount,
      capacity: this.maxQueuedLength,
      completed: this.completedCount,
      completedInWindow,
      averageCompletedRequestsPerSecond:
        completedInWindow / (throughputWindowMs / MS_PER_SECOND),
      abortCounts: { ...this.abortCounts },
      abortCountsInWindow,
      queuedAbortCounts: { ...this.queuedAbortCounts },
      queuedAbortCountsInWindow,
      activeAbortCounts: { ...this.activeAbortCounts },
      activeAbortCountsInWindow,
      queuedTasks: this.queue
        .toSorted((a, b) => a.priority() - b.priority())
        .map((task, position) => ({
          key: task.key,
          position,
          ageMs: now - task.enqueuedAt,
          priority: task.priority(),
          aborted: task.aborted,
          abortReason: task.abortReason,
        }))
        .slice(0, DEBUG_TASK_LIMIT),
      activeTasks: Array.from(this.activeTasks.values())
        .map<QueueTaskDebugInfo>((task) => ({
          key: task.key,
          position: null,
          ageMs: now - (task.startedAt ?? task.enqueuedAt),
          priority: task.priority(),
          aborted: task.aborted,
          abortReason: task.abortReason,
        }))
        .toSorted((a, b) => b.ageMs - a.ageMs)
        .slice(0, DEBUG_TASK_LIMIT),
    };
  }

  private recordTaskCompleted(): void {
    const now = performance.now();
    this.completedCount++;
    this.completedTaskTimestamps.push(now);
    this.pruneCompletedTaskTimestamps(now, THROUGHPUT_HISTORY_RETENTION_MS);
  }

  private getCompletedTaskCountInWindow(
    now: number,
    throughputWindowMs: number
  ): number {
    this.pruneCompletedTaskTimestamps(now, throughputWindowMs);
    return this.completedTaskTimestamps.length;
  }

  private pruneCompletedTaskTimestamps(now: number, windowMs: number): void {
    const minTimestamp = now - windowMs;
    while (
      this.completedTaskTimestamps.length > 0 &&
      this.completedTaskTimestamps[0] < minTimestamp
    ) {
      this.completedTaskTimestamps.shift();
    }
  }

  private admit(task: TaskDefWrapper<T>): boolean {
    if (
      this.maxQueuedLength === undefined ||
      this.queue.length < this.maxQueuedLength
    ) {
      return true;
    }

    const evictedTaskIndex = this.findEvictionCandidateIndex(task.priority());
    if (evictedTaskIndex === undefined) {
      return false;
    }

    const [evictedTask] = this.queue.splice(evictedTaskIndex, 1);
    this.abortTask(evictedTask, 'capacityEviction');
    return true;
  }

  private findEvictionCandidateIndex(
    incomingPriority: number
  ): number | undefined {
    let evictedTaskIndex: number | undefined;
    let evictedTaskPriority = Number.NEGATIVE_INFINITY;

    this.queue.forEach((task, index) => {
      const taskPriority = task.priority();
      if (task.cancelable && taskPriority > evictedTaskPriority) {
        evictedTaskIndex = index;
        evictedTaskPriority = taskPriority;
      }
    });

    if (
      evictedTaskIndex === undefined ||
      incomingPriority >= evictedTaskPriority
    ) {
      return undefined;
    }

    return evictedTaskIndex;
  }

  private preemptActiveTaskIfUseful(incomingPriority: number): void {
    if (this.activeCount < this.concurrency) {
      return;
    }

    const activeTaskToPreempt =
      this.findActivePreemptionCandidate(incomingPriority);
    if (activeTaskToPreempt) {
      this.abortTask(activeTaskToPreempt, 'preemption');
    }
  }

  private findActivePreemptionCandidate(
    incomingPriority: number
  ): TaskDefWrapper<T> | undefined {
    let activeTaskToPreempt: TaskDefWrapper<T> | undefined;
    let activeTaskPriority = Number.NEGATIVE_INFINITY;

    for (const task of this.activeTasks.values()) {
      const taskPriority = task.priority();
      if (
        task.cancelable &&
        !task.aborted &&
        taskPriority > activeTaskPriority
      ) {
        activeTaskToPreempt = task;
        activeTaskPriority = taskPriority;
      }
    }

    if (
      !activeTaskToPreempt ||
      incomingPriority + ACTIVE_PREEMPTION_MIN_PRIORITY_IMPROVEMENT >=
        activeTaskPriority
    ) {
      return undefined;
    }

    return activeTaskToPreempt;
  }

  private abortTask(task: TaskDefWrapper<T>, abortReason: AbortReason): void {
    if (task.aborted) {
      return;
    }

    task.aborted = true;
    task.abortReason = abortReason;
    this.recordTaskAborted(
      abortReason,
      task.startedAt === undefined ? 'queued' : 'active'
    );
    this.taskPromises.delete(task.key);
    task.abortController.abort();
    task.rejectTask(this.createAbortError(task.key));
  }

  private createCapacityError(key: string): Error {
    return new QueueCapacityError(key);
  }

  private createAbortError(key: string): Error {
    const error = new Error(`Task aborted: ${key}`);
    error.name = 'AbortError';
    return error;
  }

  private run(): void {
    if (this.activeCount >= this.concurrency || this.queue.length === 0) {
      return;
    }
    this.sortQueueByPriority();
    this.startQueuedTasks();
  }

  /**
   * Priorities are evaluated once per task rather than once per comparison:
   * tile priorities involve camera-distance maths and are hot during
   * navigation. Returns the highest priority (lowest number).
   */
  private sortQueueByPriority(): number {
    const ranked = this.queue.map((task) => ({
      task,
      priority: task.priority(),
    }));
    ranked.sort((a, b) => a.priority - b.priority);
    this.queue = ranked.map(({ task }) => task);
    return ranked[0]?.priority ?? Number.POSITIVE_INFINITY;
  }

  private startQueuedTasks(): void {
    while (this.activeCount < this.concurrency && this.queue.length > 0) {
      const taskDefWrapper = this.queue.shift();
      if (taskDefWrapper) {
        if (this.shouldSkipTaskStart(taskDefWrapper)) {
          this.abortTask(taskDefWrapper, 'staleBeforeStart');
          continue;
        }
        taskDefWrapper.taskWrapper().catch(() => {
          // The rejection is already passed to the user-facing promise's `reject` function.
          // This just prevents a "fire-and-forget" promise from crashing the process.
        });
      }
    }
  }

  private shouldSkipTaskStart(task: TaskDefWrapper<T>): boolean {
    return (
      task.aborted ||
      task.abortController.signal.aborted ||
      task.shouldRun?.() === false
    );
  }

  private recordTaskAborted(
    abortReason: AbortReason,
    lifecycle: 'queued' | 'active'
  ): void {
    const now = performance.now();
    this.abortCounts[abortReason]++;
    if (lifecycle === 'queued') {
      this.queuedAbortCounts[abortReason]++;
    } else {
      this.activeAbortCounts[abortReason]++;
    }
    this.abortEvents.push({ timestamp: now, reason: abortReason, lifecycle });
    this.pruneAbortEvents(now, THROUGHPUT_HISTORY_RETENTION_MS);
  }

  private getAbortCountsInWindow(
    now: number,
    windowMs: number,
    lifecycle?: 'queued' | 'active'
  ): AbortCounts {
    this.pruneAbortEvents(now, windowMs);
    const abortCountsInWindow = this.createEmptyAbortCounts();
    this.abortEvents.forEach((event) => {
      if (!lifecycle || event.lifecycle === lifecycle) {
        abortCountsInWindow[event.reason]++;
      }
    });
    return abortCountsInWindow;
  }

  private pruneAbortEvents(now: number, windowMs: number): void {
    const minTimestamp = now - windowMs;
    while (
      this.abortEvents.length > 0 &&
      this.abortEvents[0].timestamp < minTimestamp
    ) {
      this.abortEvents.shift();
    }
  }

  private createEmptyAbortCounts(): AbortCounts {
    return {
      visibility: 0,
      preemption: 0,
      timeout: 0,
      clear: 0,
      staleBeforeStart: 0,
      remove: 0,
      abortController: 0,
      capacityEviction: 0,
    };
  }
}
